import { useEffect, useState } from "react";
import { Ban, Pencil, Plus, ShieldCheck, Trash2 } from "lucide-react";

import PilotageEmptyState from "@/components/pilotage/PilotageEmptyState";
import PilotageField from "@/components/pilotage/PilotageField";
import PilotageModal from "@/components/pilotage/PilotageModal";
import PilotageTable from "@/components/pilotage/PilotageTable";
import AppErrorState from "@/components/ui/AppErrorState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/lib/api";
import {
  blockCollabAccess,
  createCollabAccess,
  deleteCollabAccess,
  listCollabAccess,
  unblockCollabAccess,
  updateCollabAccess,
} from "@/lib/settingsApi";

const ROLE_OPTIONS = [
  { value: "SUPER_ADMIN", label: "Super Admin" },
  { value: "ADMIN", label: "Admin" },
  { value: "RH", label: "RH" },
  { value: "COLLAB", label: "Collab" },
];

const ROLE_LABELS = ROLE_OPTIONS.reduce(
  (labels, option) => ({ ...labels, [option.value]: option.label }),
  {},
);

const EMPTY_FORM = {
  email: "",
  name: "",
  is_active: true,
  role: "COLLAB",
};

const ORIGIN_LABELS = {
  INTERNAL_DOMAIN: "Interne",
  EXTERNAL_MANUAL: "Externe",
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const isInternalEmail = (value) => normalizeEmail(value).endsWith("@lenvia.eu");

const isInternalProfile = (profile) => {
  if (!profile) {
    return false;
  }

  if (profile.account_origin === "INTERNAL_DOMAIN") {
    return true;
  }

  if (profile.account_origin === "EXTERNAL_MANUAL") {
    return false;
  }

  return isInternalEmail(profile.email);
};

const getProfileStatus = (profile) => {
  if (profile.is_blocked || profile.is_active === false) {
    return {
      label: "Bloque",
      className: "border-red-500/20 bg-red-500/10 text-red-200",
    };
  }

  return {
    label: "Actif",
    className: "border-emerald-500/20 bg-emerald-500/10 text-emerald-200",
  };
};

const CollabAccessPage = () => {
  const { role } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const isSuperAdmin = role === "SUPER_ADMIN";
  const showEmptyState = !loading && profiles.length === 0;
  const isEditingInternalProfile = isInternalProfile(editingProfile);

  const loadProfiles = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await listCollabAccess();
      setProfiles(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          "Impossible de charger les profils d'acces.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProfiles();
  }, []);

  const openCreateModal = () => {
    setEditingProfile(null);
    setFormValues(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEditModal = (profile) => {
    setEditingProfile(profile);
    setFormValues({
      email: profile.email || "",
      name: profile.name || "",
      is_active: profile.is_active !== false,
      role: profile.role || "COLLAB",
    });
    setModalOpen(true);
  };

  const closeModal = (nextOpen) => {
    setModalOpen(nextOpen);
    if (!nextOpen) {
      setEditingProfile(null);
      setFormValues(EMPTY_FORM);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!editingProfile && isInternalEmail(formValues.email)) {
      setError(
        "Les profils internes @lenvia.eu accedent automatiquement a la plateforme. Utilisez le blocage ou le changement de role si necessaire.",
      );
      return;
    }

    setSaving(true);
    setError("");

    try {
      if (!editingProfile) {
        await createCollabAccess({
          email: normalizeEmail(formValues.email),
          name: formValues.name.trim() || null,
          role: formValues.role,
          is_active: formValues.is_active,
        });
      } else {
        const nextPayload = {
          role: formValues.role,
        };

        if (!isEditingInternalProfile) {
          nextPayload.name = formValues.name.trim() || null;
          nextPayload.is_active = formValues.is_active;
        }

        await updateCollabAccess(editingProfile.id, {
          ...nextPayload,
        });
      }

      closeModal(false);
      await loadProfiles();
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          "Impossible d'enregistrer ce profil.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (profile) => {
    const shouldDelete = window.confirm(
      `Supprimer le profil externe "${profile.email}" ?`,
    );
    if (!shouldDelete) {
      return;
    }

    setError("");

    try {
      await deleteCollabAccess(profile.id);
      await loadProfiles();
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          "Impossible de supprimer ce profil.",
        ),
      );
    }
  };

  const toggleProfile = async (profile) => {
    setError("");

    try {
      if (profile.is_blocked) {
        await unblockCollabAccess(profile.id);
      } else {
        await blockCollabAccess(profile.id);
      }
      await loadProfiles();
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          "Impossible de mettre a jour le statut de ce profil.",
        ),
      );
    }
  };

  const renderActions = (profile) => {
    if (!isSuperAdmin) {
      return null;
    }

    const internalProfile = isInternalProfile(profile);
    const canDelete = !internalProfile && profile.can_delete;

    return (
      <div className="flex flex-wrap justify-end gap-2">
        {profile.can_edit_role ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => openEditModal(profile)}
            data-testid={`collab-access-edit-${profile.id}`}
            className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
          >
            <Pencil className="h-3.5 w-3.5" />
            Modifier
          </Button>
        ) : null}

        {profile.can_toggle_block ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => toggleProfile(profile)}
            data-testid={`collab-access-toggle-block-${profile.id}`}
            className={
              profile.is_blocked
                ? "border-emerald-500/20 text-emerald-200 hover:bg-emerald-500/10 hover:text-emerald-100"
                : "border-red-500/20 text-red-200 hover:bg-red-500/10 hover:text-red-100"
            }
          >
            <Ban className="h-3.5 w-3.5" />
            {profile.is_blocked ? "Debloquer" : "Bloquer"}
          </Button>
        ) : null}

        {canDelete ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => handleDelete(profile)}
            data-testid={`collab-access-delete-${profile.id}`}
            className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Supprimer
          </Button>
        ) : null}
      </div>
    );
  };

  return (
    <section className="space-y-6" data-testid="collab-access-page">
      <div className="rounded-3xl border border-white/10 glass p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Parametres
            </p>
            <h2 className="mt-2 text-xl font-heading font-bold text-white">
              Gestion des acces
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Profils ayant valide au moins une MFA et profils externes ajoutes
              manuellement.
            </p>
          </div>
          {!showEmptyState && isSuperAdmin ? (
            <Button
              type="button"
              onClick={openCreateModal}
              className="btn-primary"
              data-testid="collab-access-create-button"
            >
              <Plus className="h-4 w-4" />
              Ajouter un profil
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <AppErrorState message={error} /> : null}

      {showEmptyState ? (
        <PilotageEmptyState
          icon={ShieldCheck}
          title="Aucun profil"
          description={
            isSuperAdmin
              ? "Ajoutez un premier profil externe ou attendez une premiere connexion MFA."
              : "Aucun profil d'acces a afficher pour le moment."
          }
          actionLabel={isSuperAdmin ? "Ajouter un profil" : undefined}
          onAction={isSuperAdmin ? openCreateModal : undefined}
          testId="collab-access-empty-state"
        />
      ) : (
        <div data-testid="collab-access-table">
          <PilotageTable
            columns={[
              {
                key: "name",
                label: "Nom",
                render: (profile) => profile.name || "—",
              },
              {
                key: "email",
                label: "Email",
                render: (profile) => (
                  <div>
                    <p className="font-medium text-white">{profile.email}</p>
                    {profile.is_last_active_super_admin ? (
                      <p className="mt-1 text-xs text-amber-200">
                        Dernier SUPER_ADMIN actif protege
                      </p>
                    ) : null}
                  </div>
                ),
              },
              {
                key: "origin",
                label: "Origine",
                render: (profile) =>
                  ORIGIN_LABELS[profile.account_origin] || profile.account_origin,
              },
              {
                key: "role",
                label: "Role",
                render: (profile) => ROLE_LABELS[profile.role] || profile.role,
              },
              {
                key: "status",
                label: "Statut",
                render: (profile) => {
                  const status = getProfileStatus(profile);
                  return (
                    <span
                      className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${status.className}`}
                    >
                      {status.label}
                    </span>
                  );
                },
              },
              {
                key: "first_verified_at",
                label: "Premiere MFA reussie",
                render: (profile) => formatDateTime(profile.first_verified_at || profile.verified_at),
              },
              {
                key: "last_login",
                label: "Derniere connexion",
                render: (profile) => formatDateTime(profile.last_login),
              },
            ]}
            rows={profiles}
            loading={loading}
            emptyMessage="Aucun profil enregistre."
            renderActions={renderActions}
          />
        </div>
      )}

      {isSuperAdmin ? (
        <PilotageModal
          open={modalOpen}
          onOpenChange={closeModal}
          title={
            editingProfile
              ? "Modifier le profil d'acces"
              : "Ajouter un profil"
          }
          description={
            editingProfile
              ? "Mettez a jour les informations autorisees pour ce profil."
              : "Les profils externes doivent etre preautorises par un SUPER_ADMIN."
          }
          onSubmit={handleSubmit}
          submitLabel={editingProfile ? "Enregistrer" : "Ajouter"}
          isSubmitting={saving}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <PilotageField label="Email" htmlFor="collab-access-email">
              <Input
                id="collab-access-email"
                type="email"
                value={formValues.email}
                onChange={(event) =>
                  setFormValues((currentValues) => ({
                    ...currentValues,
                    email: event.target.value,
                  }))
                }
                placeholder="user@gmail.com"
                required
                readOnly={Boolean(editingProfile)}
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 read-only:opacity-70"
              />
            </PilotageField>

            <PilotageField label="Nom" htmlFor="collab-access-name">
              <Input
                id="collab-access-name"
                value={formValues.name}
                onChange={(event) =>
                  setFormValues((currentValues) => ({
                    ...currentValues,
                    name: event.target.value,
                  }))
                }
                placeholder="Nom du profil"
                readOnly={isEditingInternalProfile}
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 read-only:opacity-70"
              />
            </PilotageField>
          </div>

          {isEditingInternalProfile ? (
            <p className="text-xs text-slate-400">
              Les profils internes @lenvia.eu accedent automatiquement a la plateforme.
              Utilisez le blocage ou le changement de role si necessaire.
            </p>
          ) : null}

          <PilotageField label="Role attribue" htmlFor="collab-access-role">
            <select
              id="collab-access-role"
              value={formValues.role}
              onChange={(event) =>
                setFormValues((currentValues) => ({
                  ...currentValues,
                  role: event.target.value,
                }))
              }
              className="flex h-11 w-full rounded-xl border border-white/10 bg-white/5 px-3 text-sm text-white outline-none transition focus:border-lenvia-blue"
            >
              {ROLE_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  className="bg-slate-950 text-white"
                >
                  {option.label}
                </option>
              ))}
            </select>
          </PilotageField>

          {isEditingInternalProfile ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
              <p className="text-sm font-medium text-white">Statut interne</p>
              <p className="mt-1 text-xs text-slate-500">
                Les profils internes se gerent via les actions Bloquer / Debloquer.
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
              <div>
                <p className="text-sm font-medium text-white">Profil actif</p>
                <p className="mt-1 text-xs text-slate-500">
                  Le compte peut demarrer le flux de connexion OTP/MFA.
                </p>
              </div>
              <Switch
                checked={formValues.is_active}
                onCheckedChange={(checked) =>
                  setFormValues((currentValues) => ({
                    ...currentValues,
                    is_active: checked === true,
                  }))
                }
              />
            </div>
          )}
        </PilotageModal>
      ) : null}
    </section>
  );
};

export default CollabAccessPage;
